/**
 * Operations-wall full-screen toggle (P116).
 *
 * A reusable chromeless full-screen for the command-view surfaces
 * (constellation / capmap / cascade / board): click to fill the screen
 * with just that view + its own controls -- no Marsad top-nav or
 * breadcrumb -- for a projector / SOC wall / boardroom display. Uses the
 * browser Fullscreen API on a target element ref; Escape exits natively.
 */
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, type RefObject } from "react";

/** Tracks + toggles fullscreen on the given element. */
export function useFullscreen(ref: RefObject<HTMLElement | null>): {
  isFullscreen: boolean;
  toggle: () => void;
} {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement != null);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggle = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void ref.current?.requestFullscreen?.();
    }
  }, [ref]);

  return { isFullscreen, toggle };
}

export function FullscreenToggle({
  targetRef,
  className,
}: {
  targetRef: RefObject<HTMLElement | null>;
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { isFullscreen, toggle } = useFullscreen(targetRef);
  return (
    <button
      type="button"
      data-testid="fullscreen-toggle"
      data-fullscreen={isFullscreen}
      onClick={toggle}
      aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
      className={
        className ??
        "rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10"
      }
    >
      {isFullscreen
        ? t("screens.fullscreenToggle.exitFullScreen")
        : t("screens.fullscreenToggle.fullScreen")}
    </button>
  );
}
